import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Save } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';

const en = (j: any) => (typeof j === 'string' ? j : j?.en || '');

function slugify(s: string) {
  return s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

function TaxonomyTable({ table, label }: { table: 'news_categories' | 'news_tags'; label: string }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ['taxonomy', table],
    queryFn: async () => {
      const { data } = await supabase.from(table).select('*').order('slug');
      return data || [];
    },
  });

  const add = async () => {
    if (!name.trim()) return;
    const s = (slug || slugify(name)).trim();
    const { error } = await supabase.from(table).insert({ slug: s, name: { en: name.trim() } } as any);
    if (error) toast({ title: 'Add failed', description: error.message, variant: 'destructive' });
    else { setName(''); setSlug(''); qc.invalidateQueries({ queryKey: ['taxonomy', table] }); }
  };

  const update = async (id: string, patch: any) => {
    const { error } = await supabase.from(table).update(patch).eq('id', id);
    if (error) toast({ title: 'Update failed', description: error.message, variant: 'destructive' });
    else qc.invalidateQueries({ queryKey: ['taxonomy', table] });
  };

  const remove = async (id: string) => {
    if (!confirm('Delete?')) return;
    const { error } = await supabase.from(table).delete().eq('id', id);
    if (error) toast({ title: 'Delete failed', description: error.message, variant: 'destructive' });
    else qc.invalidateQueries({ queryKey: ['taxonomy', table] });
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{label}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-2 border-b pb-3">
          <div className="flex-1 min-w-[140px]">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => { setName(e.target.value); if (!slug) setSlug(slugify(e.target.value)); }} />
          </div>
          <div className="flex-1 min-w-[140px]">
            <Label>Slug</Label>
            <Input value={slug} onChange={(e) => setSlug(slugify(e.target.value))} />
          </div>
          <Button onClick={add}><Plus className="h-4 w-4 mr-1" /> Add</Button>
        </div>

        {isLoading ? <div className="text-sm text-muted-foreground">Loading…</div> : (
          <div className="space-y-1">
            {rows.length === 0 && <div className="text-sm text-muted-foreground">None yet.</div>}
            {rows.map((r: any) => (
              <Row key={r.id} row={r} onSave={update} onDelete={remove} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function Row({ row, onSave, onDelete }: any) {
  const [name, setName] = useState(en(row.name));
  const [slug, setSlug] = useState(row.slug);
  const [active, setActive] = useState(!!row.is_active);
  const dirty = name !== en(row.name) || slug !== row.slug || active !== row.is_active;

  return (
    <div className="flex flex-wrap items-center gap-2 p-2 rounded hover:bg-muted/30">
      <Input className="flex-1 min-w-[120px]" value={name} onChange={(e) => setName(e.target.value)} />
      <Input className="w-40" value={slug} onChange={(e) => setSlug(slugify(e.target.value))} />
      <div className="flex items-center gap-1.5">
        <Switch checked={active} onCheckedChange={setActive} />
        <span className="text-xs text-muted-foreground">Active</span>
      </div>
      <Button size="sm" variant="outline" disabled={!dirty} onClick={() => onSave(row.id, { name: { en: name }, slug, is_active: active })}>
        <Save className="h-3.5 w-3.5" />
      </Button>
      <Button size="sm" variant="ghost" onClick={() => onDelete(row.id)}>
        <Trash2 className="h-3.5 w-3.5 text-destructive" />
      </Button>
    </div>
  );
}

export default function NewsTaxonomy() {
  return (
    <div className="container max-w-5xl py-6 space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold">News categories & tags</h1>
        <p className="text-sm text-muted-foreground">Curate the picker shown in the editor and the public News filter bar.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <TaxonomyTable table="news_categories" label="Categories" />
        <TaxonomyTable table="news_tags" label="Tags" />
      </div>
    </div>
  );
}
